import React, { useState, useEffect } from 'react';
import { makeStyles, Typography } from '@material-ui/core';
import { StylesProvider } from '@material-ui/core/styles';
import MaterialTable from 'material-table';

import { createTableIcons } from '../../../shared/styles/createTableIcons';
import { generateClassName } from '../../../shared/styles/generateClassName';
import SnackConfig from '../../../shared/config/snackConfig';
import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SpinnerOverlay from '../../../shared/components/SpinnerOverlay/SpinnerOverlay';
import { queryAlarmPolicies, updateAlarmPolicy, UpdateAlarmPolicyRequest } from '../../../shared/services/alarmPolicy';

const useStyles = makeStyles(() => ({
  container: {
    width: '100%',
    padding: '20px',
    boxSizing: 'border-box',
  },
  maxBorderBox: {
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
}));

interface TableAlarmPolicyData {
  id: number;
  name: string;
  numberOfAnomalies: number;
  windowSize: string;
  channelName: string;
}

const AlarmPolicy: React.FC = () => {
  const classes = useStyles();
  const [alarms, setAlarms] = useState<TableAlarmPolicyData[]>([]);
  const [loadingAlarms, setLoadingAlarms] = useState<boolean>(false);
  const [snackConfig, setSnackConfig] = useState<SnackConfig>(new SnackConfig({}));

  useEffect(() => {
    void (async () => {
      try {
        setLoadingAlarms(true);
        const response = await queryAlarmPolicies();
        if (response && response.alarmPolicies) {
          const { alarmPolicies } = response;
          setAlarms(
            alarmPolicies.map(({ id, name, channelName, policy }) => ({
              id,
              name,
              channelName,
              numberOfAnomalies: policy.numberOfAnomalies,
              windowSize: policy.windowSize,
            }))
          );
        }
      } catch (error) {
        setSnackConfig(
          new SnackConfig({
            open: true,
            message: JSON.stringify((error as Error).message),
          })
        );
      }
      setLoadingAlarms(false);
    })();
  }, []);

  const update = async (newData: TableAlarmPolicyData): Promise<boolean> => {
    let successfullyUpdated = false;
    const request: UpdateAlarmPolicyRequest = {
      id: newData.id,
      name: newData.name,
      windowSize: newData.windowSize,
      numberOfAnomalies: newData.numberOfAnomalies,
    };
    try {
      const result = await updateAlarmPolicy(request);
      if (result && result.updateAlarmPolicy) {
        successfullyUpdated = true;
        const index = alarms.findIndex((alarm) => alarm.id === newData.id);
        const { id, name, channelName, policy } = result.updateAlarmPolicy;
        const updatedAlarmPolicy: TableAlarmPolicyData = {
          id,
          name,
          numberOfAnomalies: policy.numberOfAnomalies,
          windowSize: newData.windowSize,
          channelName,
        };
        const updatedAlarms = alarms.slice();
        updatedAlarms[index] = updatedAlarmPolicy;
        setAlarms(updatedAlarms);
      }
    } catch (error) {
      setSnackConfig(
        new SnackConfig({
          open: true,
          message: JSON.stringify((error as Error).message),
        })
      );
    }
    return successfullyUpdated;
  };

  return (
    <div className={classes.container}>
      <div className={classes.maxBorderBox}>
        <SpinnerOverlay active={loadingAlarms}>
          <StylesProvider generateClassName={generateClassName()}>
            <MaterialTable
              title=""
              options={{
                emptyRowsWhenPaging: false,
              }}
              columns={[
                { title: 'Name', field: 'name', type: 'string', searchable: true, sorting: true },
                {
                  title: 'Number of Anomalies',
                  field: 'numberOfAnomalies',
                  type: 'numeric',
                  searchable: false,
                  sorting: false,
                },
                {
                  title: (
                    <span>
                      Window size
                      <div>
                        <Typography color="textSecondary" variant="caption">
                          Eg: 3m for 3 mins, 100s for 100 secs.
                        </Typography>
                      </div>
                    </span>
                  ),
                  field: 'windowSize',
                  type: 'string',
                  searchable: false,
                  sorting: false,
                },
                {
                  title: 'Notification Channel',
                  field: 'channelName',
                  type: 'string',
                  searchable: true,
                  sorting: true,
                  editable: 'never',
                },
              ]}
              data={alarms}
              icons={createTableIcons()}
              editable={{
                onRowUpdate: update,
              }}
            />
          </StylesProvider>
        </SpinnerOverlay>
      </div>
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default AlarmPolicy;
